import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Crown, Loader2, Home, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        setPaymentInfo(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Refresh user to get updated subscription
          if (refreshUser) {
            await refreshUser();
          }
          // Celebrate!
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 },
            colors: ['#1E4ED8', '#60A5FA', '#FFD700', '#FFA500']
          });
        } else if (response.data.payment_status === 'unpaid') {
          setStatus('pending');
        } else {
          setStatus('error');
        }
      } catch (err) {
        setStatus('error');
      }
    };

    checkStatus();
  }, [sessionId, api, refreshUser]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#1E4ED8] mx-auto mb-4 animate-spin" />
            <h1 className="font-fraunces text-2xl font-bold mb-2">Activation en cours...</h1>
            <p className="text-gray-500">Preparation de ton abonnement</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="w-24 h-24 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center mx-auto mb-6 shadow-xl"
            >
              <Crown className="w-12 h-12 text-white" />
            </motion.div>
            
            <h1 className="font-fraunces text-3xl font-bold mb-3">
              Bienvenue dans le club!
            </h1>
            <p className="text-gray-600 mb-6">
              Ton abonnement est maintenant actif. Profite de tous les avantages!
            </p>
            
            <div className="bg-gradient-to-br from-[#1E4ED8] to-[#60A5FA] rounded-2xl p-6 mb-6 text-white">
              <Star className="w-8 h-8 mx-auto mb-3" />
              <p className="text-sm opacity-80 mb-2">Tu es maintenant</p>
              <p className="font-fraunces text-3xl font-bold">
                {paymentInfo?.amount_total >= 5000 ? 'Pro Creator' : 'Premium'}
              </p>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm">
                  +{paymentInfo?.amount_total >= 5000 ? '50' : '25'} BARKA bonus!
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
              <div className="bg-[#F8F5F2] rounded-xl p-3">
                <CheckCircle className="w-5 h-5 text-[#4F6F52] mx-auto mb-1" />
                <p className="text-gray-600">Recettes premium</p>
              </div>
              <div className="bg-[#F8F5F2] rounded-xl p-3">
                <CheckCircle className="w-5 h-5 text-[#4F6F52] mx-auto mb-1" />
                <p className="text-gray-600">Badge verifie</p>
              </div>
              <div className="bg-[#F8F5F2] rounded-xl p-3">
                <CheckCircle className="w-5 h-5 text-[#4F6F52] mx-auto mb-1" />
                <p className="text-gray-600">Support prioritaire</p>
              </div>
              <div className="bg-[#F8F5F2] rounded-xl p-3">
                <CheckCircle className="w-5 h-5 text-[#4F6F52] mx-auto mb-1" />
                <p className="text-gray-600">Revenus augmentes</p>
              </div>
            </div>

            <Button 
              onClick={() => navigate('/home')}
              className="w-full bg-[#1E4ED8] hover:bg-[#1E40AF] text-white rounded-full h-12"
            >
              <Home className="w-4 h-4 mr-2" />
              Commencer a explorer
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-spin" />
            <h1 className="font-fraunces text-2xl font-bold mb-2">Activation en cours...</h1>
            <p className="text-gray-500 mb-6">Ton abonnement sera active dans quelques instants</p>
            <Button onClick={() => navigate('/home')} className="rounded-full">
              Retour a l'accueil
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D9381E]/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">😕</span>
            </div>
            <h1 className="font-fraunces text-2xl font-bold mb-2">Oups!</h1>
            <p className="text-gray-500 mb-6">
              Impossible d'activer l'abonnement. Si tu as ete debite, contacte le support.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/support')} className="flex-1 rounded-full">
                Support
              </Button>
              <Button onClick={() => navigate('/subscriptions')} variant="outline" className="flex-1 rounded-full">
                Reessayer
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
